import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { requireAuth } from '@/lib/auth-guard'
import { resolveClientBySlug } from '@/lib/api/resolve-client'
import { ensureWorkspaces, refetchWorkspaces } from '@/lib/api/resolve-workspaces'
import { directStoreToClient } from '@/lib/resolve-direct-client'
import { type OrgAccess } from '@/lib/org-access'
import type { Client, Organization, User } from '@/modules/shared/types'

function NotFound() {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <h2 className="text-2xl font-bold text-foreground">Page not found</h2>
      <p className="mt-2 text-sm text-muted">The page you're looking for doesn't exist.</p>
      <Link to="/" className="mt-4 text-sm font-medium text-primary hover:underline">
        Go home
      </Link>
    </div>
  )
}

interface ClientRouteContext {
  org: Organization
  client: Client
  currentUser: User
  orgAccess: OrgAccess
}

export const Route = createFileRoute('/$orgSlug/$clientSlug')({
  beforeLoad: async ({ params, context }): Promise<ClientRouteContext> => {
    await requireAuth()
    const { org, currentUser, orgAccess, queryClient } = context as {
      org: Organization
      currentUser: User
      orgAccess: OrgAccess
      queryClient: import('@tanstack/react-query').QueryClient
    }

    if (orgAccess === 'store-only') {
      // Direct members resolve against their own store list — the agency
      // clients endpoint would 403 for them.
      const workspaces = await ensureWorkspaces(queryClient)
      const findDirect = (ws: typeof workspaces) =>
        ws.directStores.find((s) => s.orgSlug === params.orgSlug && s.slug === params.clientSlug)

      let direct = findDirect(workspaces)
      if (!direct) {
        // A miss may just be a stale cache (store granted after the cache
        // was filled, in another tab, or by a teammate) — force one
        // authoritative read before giving up. See `resolve-workspaces.ts`.
        const fresh = await refetchWorkspaces(queryClient)
        direct = findDirect(fresh)
      }
      if (!direct) throw notFound()
      return { org, client: directStoreToClient(direct), currentUser, orgAccess }
    }

    // Cache-first (SAME key the ClientSwitcher uses) so the guard shares that
    // cache — but a miss is re-checked against the server before we 404, since
    // a stale list would otherwise hide a just-created store. See
    // `resolveClientBySlug`.
    const client = await resolveClientBySlug(queryClient, org.id, params.clientSlug)
    if (!client) throw notFound()

    // Onboarding (connecting platforms) is OPT-IN, never a passive gate: no one
    // connects their store to us before signing. So we never force-redirect a
    // store into the connect-platforms wizard here. Every store lands in its
    // app shell; the wizard is reached deliberately (an explicit "Connect
    // accounts" action). The client index route sends un-onboarded stores to
    // the Scenario Planner (data-free), and onboarded stores to their
    // dashboard.

    return { org, client, currentUser, orgAccess }
  },
  notFoundComponent: NotFound,
})
